import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { BrandShopByCategory } from '../components/brand-store/BrandShopByCategory';
import { BrandFeaturedProducts } from '../components/brand-store/BrandFeaturedProducts';
import { BrandOffers } from '../components/brand-store/BrandOffers';
import { BrandTopRated } from '../components/brand-store/BrandTopRated';
import { BrandComboSets } from '../components/brand-store/BrandComboSets';
import { BrandVideos } from '../components/brand-store/BrandVideos';
import { BrandQuickActions } from '../components/brand-store/BrandQuickActions';
import { BrandTimeline } from '../components/brand-store/BrandTimeline';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { PullToRefresh } from '../components/shared/PullToRefresh';
import { toast } from 'sonner';
import { StarIcon, CheckCircleIcon, TruckIcon, ShieldCheckIcon, HeartIcon } from 'lucide-react';
const sectionTabs = [{
  id: 'featured',
  label: 'Featured'
}, {
  id: 'categories',
  label: 'Categories'
}, {
  id: 'offers',
  label: 'Offers'
}, {
  id: 'top-rated',
  label: 'Top Rated'
}, {
  id: 'combos',
  label: 'Combos'
}, {
  id: 'videos',
  label: 'Videos'
}, {
  id: 'about',
  label: 'About'
}];
export function BrandStorePage() {
  const {
    brandSlug
  } = useParams<{
    brandSlug: string;
  }>();
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [following, setFollowing] = useState(false);
  const [activeTab, setActiveTab] = useState('featured');
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const brandName = brandSlug?.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'Brand';
  const brandData = {
    name: brandName,
    logo: 'https://images.unsplash.com/photo-1556228578-0d85b1a4d571?w=400',
    description: 'Premium skincare powered by thermal spa water and advanced dermatological science',
    since: '1931',
    tagline: 'Dermatological Beauty',
    stats: [{
      label: 'Products',
      value: '150+'
    }, {
      label: 'Countries',
      value: '50+'
    }, {
      label: 'Rating',
      value: '4.8/5'
    }, {
      label: 'Reviews',
      value: '25K+'
    }],
    features: [{
      icon: <CheckCircleIcon className="w-4 h-4" data-id="element-6226" data-mp="1j4zptjg6y88k" />,
      text: '100% Authentic'
    }, {
      icon: <TruckIcon className="w-4 h-4" data-id="element-6227" data-mp="1f34pah633yvu" />,
      text: 'Fast Delivery'
    }, {
      icon: <ShieldCheckIcon className="w-4 h-4" data-id="element-6228" data-mp="1a49z2u5sf81p" />,
      text: 'Quality Guaranteed'
    }, {
      icon: <StarIcon className="w-4 h-4" data-id="element-6229" data-mp="26uagg83tum8z" />,
      text: 'Dermatologist Recommended'
    }]
  };
  const handleTabClick = (id: string) => {
    setActiveTab(id);
    const el = document.getElementById(`brand-section-${id}`);
    if (!el) return;
    const offset = window.innerWidth >= 768 ? 240 : 170;
    const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({
      top,
      behavior: 'smooth'
    });
  };
  const handleFollow = () => {
    setFollowing(v => {
      const next = !v;
      toast.success(next ? `Following ${brandData.name}` : `Unfollowed ${brandData.name}`);
      return next;
    });
  };
  // Track scroll for sticky compact header + active tab
  useEffect(() => {
    const onScroll = () => {
      setShowStickyHeader(window.scrollY > 320);
      const viewportMid = window.innerHeight / 2;
      let current = 'featured';
      for (const t of sectionTabs) {
        const el = document.getElementById(`brand-section-${t.id}`);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.top <= viewportMid && r.bottom >= viewportMid) {
          current = t.id;
          break;
        }
      }
      setActiveTab(current);
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return <HelmetProvider data-id="element-6230" data-mp="37uzqyyb438w1">
      <SEO title={`${brandName} Products`} description={`Shop authentic ${brandName} products at Innova Pharmacy. Fast delivery across Saudi Arabia with secure checkout and 24/7 support.`} canonical={`/en/brand/${brandSlug}/`} data-id="element-6231" data-mp="3cnj1fmei5nwm" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-6232" data-mp="3i0r6bfb07bl5">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-6233" data-mp="3vrwbsy11yy3e">
          <AnnouncementBar data-id="element-6234" data-mp="3qtkzmgnacbi0" />
          <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-6235" data-mp="3quckoxj9aqu1" />
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-6236" data-mp="2z253nvzu9cf4" />
        </div>
        <main className="flex-grow pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-6237" data-mp="3821yyggqh9j5">
          {/* Mobile Header Overlay */}
          <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-6238" data-mp="nckyjzrsx13r">
            <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} alwaysWhiteBackground={true} data-id="element-6239" data-mp="ncckw5nutrri" />
          </div>

          {/* Mobile Sticky Compact Brand Header — appears on scroll */}
          <div className={`md:hidden fixed top-[112px] left-0 right-0 z-40 bg-white/95 backdrop-blur border-b border-gray-200 shadow-sm transition-transform duration-200 ${showStickyHeader ? 'translate-y-0' : '-translate-y-full'}`} data-id="element-6240" data-mp="14ei5pv96r7ef">
            <div className="flex items-center justify-between px-4 py-2.5" data-id="element-6241" data-mp="gnusqvkexrl0">
              <div className="flex items-center gap-2.5 min-w-0" data-id="element-6242" data-mp="lnh7j9fw3fvx">
                <div className="w-9 h-9 rounded-lg bg-white border border-gray-200 overflow-hidden flex-shrink-0" data-id="element-6243" data-mp="lm6111v27u14">
                  <img src={brandData.logo} alt={brandData.name} className="w-full h-full object-contain" data-id="element-6244" data-mp="qg0irzr3tyxk" />
                </div>
                <div className="min-w-0" data-id="element-6245" data-mp="3u0xxwnub7aou">
                  <p className="text-sm font-bold text-gray-900 truncate" data-id="element-6246" data-mp="3miq8joksr8tn">
                    {brandData.name}
                  </p>
                  <p className="text-[11px] text-gray-500 truncate" data-id="element-6247" data-mp="3hlg8lezs7erk">
                    Since {brandData.since} • {brandData.stats[2].value} ★
                  </p>
                </div>
              </div>
              <button onClick={handleFollow} className={`flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${following ? 'bg-gray-100 text-gray-700' : 'bg-[#1E2B55] text-white'}`} data-id="element-6248" data-mp="37olcf2qzdqgg">
                <HeartIcon className={`w-3.5 h-3.5 ${following ? 'fill-red-500 text-red-500' : ''}`} data-id="element-6249" data-mp="2tvlyh56uk4b0" />
                {following ? 'Following' : 'Follow'}
              </button>
            </div>
          </div>

          <h1 className="sr-only" data-id="element-6250" data-mp="2fvbb4c4qx7ul">{brandName} - Official Products</h1>

          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Brands',
          href: '/brands'
        }, {
          label: brandName
        }]} title={brandName} description={brandData.description} data-id="element-6251" data-mp="2fxfm1e12b74d" />

          {/* Brand Hero — Compact on mobile */}
          <div className="bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] text-white py-5 md:py-16" data-id="element-6252" data-mp="3l0rb53g0bi6o">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-6253" data-mp="3pt1a18z3nsl3">
              {/* Mobile compact layout */}
              <div className="md:hidden" data-id="element-6254" data-mp="3b4wpdzghl6zo">
                <div className="flex items-center gap-3 mb-4" data-id="element-6255" data-mp="3g26mm1a4amy2">
                  <div className="bg-white rounded-xl p-2 shadow-lg flex-shrink-0" data-id="element-6256" data-mp="3g2ps2iikziv7">
                    <img src={brandData.logo} alt={brandData.name} className="w-16 h-16 object-contain" data-id="element-6257" data-mp="3236ycuqioa2f" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-6258" data-mp="2ng88h47b5npt">
                    <div className="inline-block bg-white/20 px-2 py-0.5 rounded-full text-[10px] font-medium mb-1" data-id="element-6259" data-mp="2ihn7h2k8f00b">
                      Since {brandData.since}
                    </div>
                    <h2 className="text-lg font-bold leading-tight truncate" data-id="element-6260" data-mp="2rh1wryonx4j8">
                      {brandData.name}
                    </h2>
                    <p className="text-xs text-blue-100 truncate" data-id="element-6261" data-mp="36d9emsclltvc">
                      {brandData.tagline}
                    </p>
                  </div>
                  <button onClick={handleFollow} className={`flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${following ? 'bg-white/20 text-white border border-white/30' : 'bg-[#fdc300] text-[#1E2B55]'}`} data-id="element-6262" data-mp="3f7nsqrle6ycv">
                    <HeartIcon className={`w-3.5 h-3.5 ${following ? 'fill-white' : ''}`} data-id="element-6263" data-mp="3k70h5grwwdhk" />
                    {following ? 'Following' : 'Follow'}
                  </button>
                </div>
                {/* Stats row */}
                <div className="grid grid-cols-4 gap-2 mb-3" data-id="element-6264" data-mp="20qnsjwtug5tg">
                  {brandData.stats.map((stat, index) => <div key={index} className="text-center bg-white/10 rounded-lg py-2" data-id="element-6265" data-mp="1vsm0wnicqy6d" data-mpi={index}>
                      <div className="text-base font-bold" data-id="element-6266" data-mp="2sfqz5j97ds99">{stat.value}</div>
                      <div className="text-[10px] text-blue-200" data-id="element-6267" data-mp="2oh9owquhk1nm">
                        {stat.label}
                      </div>
                    </div>)}
                </div>
                {/* Features as horizontal pills */}
                <div className="-mx-4 px-4" data-id="element-6268" data-mp="182rno4u4f7ll">
                  <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1" data-id="element-6269" data-mp="183t7oyt474fg">
                    {brandData.features.map((feature, index) => <div key={index} className="flex-shrink-0 flex items-center gap-1.5 bg-white/15 px-3 py-1.5 rounded-full text-[11px] whitespace-nowrap" data-id="element-6270" data-mp="szqii72xgr84" data-mpi={index}>
                        {feature.icon}
                        <span data-id="element-6271" data-mp="12vjspjt6c8mq">{feature.text}</span>
                      </div>)}
                  </div>
                </div>
              </div>

              {/* Desktop layout (unchanged) */}
              <div className="hidden md:flex flex-col md:flex-row items-center gap-6 md:gap-12" data-id="element-6272" data-mp="1gosx1629m3ye">
                <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl" data-id="element-6273" data-mp="1lnw2grm5qc6w">
                  <img src={brandData.logo} alt={brandData.name} className="w-32 h-32 md:w-48 md:h-48 object-contain" data-id="element-6274" data-mp="1llt33gz476pi" />
                </div>
                <div className="flex-1 text-center md:text-left" data-id="element-6275" data-mp="3sec3ucq1hhzb">
                  <div className="inline-block bg-white bg-opacity-20 px-4 py-2 rounded-full text-sm font-medium mb-4" data-id="element-6276" data-mp="3selum2iwrt62">
                    Since {brandData.since}
                  </div>
                  <h2 className="text-3xl md:text-5xl font-bold mb-3" data-id="element-6277" data-mp="3fg48e648dys9">
                    {brandData.name}
                  </h2>
                  <p className="text-lg md:text-xl text-blue-100 mb-2" data-id="element-6278" data-mp="3u9h7rdsprddl">
                    {brandData.tagline}
                  </p>
                  <p className="text-sm md:text-base text-blue-200 mb-6 max-w-2xl" data-id="element-6279" data-mp="76m9ywua3b7x">
                    {brandData.description}
                  </p>
                  <div className="grid grid-cols-4 gap-4 md:gap-6 mb-6" data-id="element-6280" data-mp="27rgn0yoq49k">
                    {brandData.stats.map((stat, index) => <div key={index} className="text-center" data-id="element-6281" data-mp="by4ajjse39sd" data-mpi={index}>
                        <div className="text-2xl md:text-3xl font-bold mb-1" data-id="element-6282" data-mp="bzxg0vr9nnys">
                          {stat.value}
                        </div>
                        <div className="text-xs md:text-sm text-blue-200" data-id="element-6283" data-mp="lwtfkbz6szr7">
                          {stat.label}
                        </div>
                      </div>)}
                  </div>
                  <div className="flex flex-wrap justify-center md:justify-start gap-3 md:gap-4" data-id="element-6284" data-mp="19ician19z1k4">
                    {brandData.features.map((feature, index) => <div key={index} className="flex items-center gap-2 bg-white bg-opacity-20 px-3 md:px-4 py-2 rounded-full text-xs md:text-sm" data-id="element-6285" data-mp="10ih3874lmmet" data-mpi={index}>
                        {feature.icon}
                        <span data-id="element-6286" data-mp="qluidic9vvcs">{feature.text}</span>
                      </div>)}
                  </div>
                  <button onClick={handleFollow} className={`mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-colors ${following ? 'bg-white/20 text-white border border-white/30' : 'bg-[#fdc300] text-[#1E2B55] hover:bg-[#ffd84d]'}`} data-id="element-6287" data-mp="guq30hioq8hf">
                    <HeartIcon className={`w-5 h-5 ${following ? 'fill-white' : ''}`} data-id="element-6288" data-mp="1a7zj5pmzd0uh" />
                    {following ? 'Following' : 'Follow Brand'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Sticky Section Tabs — mobile only */}
          <div className="md:hidden sticky top-[112px] z-30 bg-white border-b border-gray-200 shadow-sm" data-id="element-6289" data-mp="wft3e4nqovvk">
            <div className="overflow-x-auto scrollbar-hide px-4 py-2" data-id="element-6290" data-mp="ri9nz1fb2d21">
              <div className="flex gap-2" data-id="element-6291" data-mp="mh3x7957gzv5">
                {sectionTabs.map(tab => <button key={tab.id} onClick={() => handleTabClick(tab.id)} className={`whitespace-nowrap px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${activeTab === tab.id ? 'bg-[#1E2B55] text-white shadow-sm' : 'bg-gray-100 text-gray-700 active:bg-gray-200'}`} data-id="element-6292" data-mp="ihbkqlcoy9ki">
                    {tab.label}
                  </button>)}
              </div>
            </div>
          </div>

          <PullToRefresh onRefresh={async () => {
          await new Promise(r => setTimeout(r, 1000));
        }} data-id="element-6293" data-mp="2kd2dvc6tuq9g">
            {/* Quick Actions */}
            <div className="py-2 md:py-8" data-id="element-6294" data-mp="2u45bu54gmp4i">
              <BrandQuickActions brandSlug={brandSlug || ''} brandName={brandData.name} data-id="element-6295" data-mp="2u33whogevggz" />
            </div>
            {/* Featured Products */}
            <div id="brand-section-featured" className="py-2 md:py-8 scroll-mt-32 transition-all" data-id="element-6296" data-mp="21fhx6se7uxzx">
              <BrandFeaturedProducts brandSlug={brandSlug || ''} data-id="element-6297" data-mp="2afo600yyz9sr" />
            </div>
            {/* Shop by Category */}
            <div id="brand-section-categories" className="py-2 md:py-8 scroll-mt-32 transition-all" data-id="element-6298" data-mp="3htrb0sku14ci">
              <BrandShopByCategory brandSlug={brandSlug || ''} data-id="element-6299" data-mp="3lt8j7trn8fld" />
            </div>
            {/* Special Offers */}
            <div id="brand-section-offers" className="py-2 md:py-8 scroll-mt-32 transition-all" data-id="element-6300" data-mp="1ms5zgboggkxt">
              <BrandOffers brandSlug={brandSlug || ''} data-id="element-6301" data-mp="1vr9jbhvg54dd" />
            </div>
            {/* Top Rated */}
            <div id="brand-section-top-rated" className="py-2 md:py-8 scroll-mt-32 transition-all" data-id="element-6302" data-mp="1qrestffl2gsu">
              <BrandTopRated brandSlug={brandSlug || ''} data-id="element-6303" data-mp="132c09cjev3jk" />
            </div>
            {/* Combo Sets */}
            <div id="brand-section-combos" className="py-2 md:py-8 scroll-mt-32 transition-all" data-id="element-6304" data-mp="yye2p69qi0sm">
              <BrandComboSets brandSlug={brandSlug || ''} data-id="element-6305" data-mp="180ncftx6hq4y" />
            </div>
            {/* Brand Videos */}
            <div id="brand-section-videos" className="py-2 md:py-8 scroll-mt-32 transition-all" data-id="element-6306" data-mp="kat15gsic9mt">
              <BrandVideos brandSlug={brandSlug || ''} data-id="element-6307" data-mp="fc7yzbtp3fvq" />
            </div>
            {/* About / Timeline */}
            <div id="brand-section-about" className="py-4 md:py-12 bg-white scroll-mt-32 transition-all" data-id="element-6308" data-mp="2qxqo91bkwxr9">
              <BrandTimeline brand={{
              name: brandData.name,
              since: brandData.since
            }} data-id="element-6309" data-mp="2hzy7dsq7li1r" />
            </div>
          </PullToRefresh>
        </main>
        <Footer data-id="element-6310" data-mp="3aoc5s9l7mgdu" />
        <MobileFooterNav data-id="element-6311" data-mp="3alpymb28e2w5" />
        <PharmacistChat data-id="element-6312" data-mp="3epfn70iwokh6" />
        <div className="h-16 md:hidden" data-id="element-6313" data-mp="3eppcsn01gpmc"></div>

        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-6314" data-mp="3enbhcd1g5be9" />
      </div>
    </HelmetProvider>;
}